import { defineComponent as _defineComponent } from 'vue';
import "ui/hoverhighlight";
import { faSquare } from "@fortawesome/free-regular-svg-icons";
import { faMinus, faSortAlphaDown, faTimes, faUndo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BAlert, BButton, BButtonGroup } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import draggable from "vuedraggable";
import { useConfirmDialog } from "@/composables/confirmDialog";
import { Toast } from "@/composables/toast";
import STATES from "@/mvc/dataset/states";
import { useDatatypesMapperStore } from "@/stores/datatypesMapperStore";
import localize from "@/utils/localization";
import FormSelectMany from "../Form/Elements/FormSelectMany/FormSelectMany.vue";
import HelpText from "../Help/HelpText.vue";
import CollectionCreator from "@/components/Collections/common/CollectionCreator.vue";
import DatasetCollectionElementView from "@/components/Collections/ListDatasetCollectionElementView.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'ListCollectionCreator',
    props: {
        historyId: null,
        initialElements: null,
        defaultHideSourceItems: { type: Boolean },
        fromSelection: { type: Boolean },
        extensions: null
    },
    emits: ["clicked-create", "on-cancel"],
    setup(__props, { emit }) {
        const props = __props;
        const NOT_VALID_ELEMENT_MSG = localize("is not a valid element for this collection");
        const state = ref("build");
        const duplicateNames = ref([]);
        const invalidElements = ref([]);
        const workingElements = ref([]);
        const selectedDatasetElements = ref([]);
        const hideSourceItems = ref(props.defaultHideSourceItems || false);
        const atLeastOneElement = ref(true);
        const atLeastOneDatasetIsSelected = computed(() => {
            return selectedDatasetElements.value.length > 0;
        });
        const getSelectedDatasetElements = computed(() => {
            return selectedDatasetElements.value;
        });
        const noMoreValidDatasets = computed(() => {
            return workingElements.value.length == 0;
        });
        const returnInvalidElementsLength = computed(() => {
            return invalidElements.value.length > 0;
        });
        const returnInvalidElements = computed(() => {
            return invalidElements.value;
        });
        const noInitialElements = computed(() => {
            return props.initialElements.length == 0;
        });
        const showDuplicateError = computed(() => {
            return duplicateNames.value.length > 0;
        });
        const allElementsAreInvalid = computed(() => {
            return props.initialElements.length == invalidElements.value.length;
        });
        /** If not `fromSelection`, the list of elements that will become the collection */
        const inListElements = ref([]);
        // variables for datatype mapping and then filtering
        const datatypesMapperStore = useDatatypesMapperStore();
        const datatypesMapper = computed(() => datatypesMapperStore.datatypesMapper);
        /** Are we filtering by datatype? */
        const filterExtensions = computed(() => !!datatypesMapper.value && !!props.extensions?.length);
        /** Does `inListElements` have elements with different extensions? */
        const listHasMixedExtensions = computed(() => {
            const extensions = new Set(inListElements.value.map((e) => e.extension));
            return extensions.size > 1;
        });
        // ----------------------------------------------------------------------- process raw list
        /** set up main data */
        function _elementsSetUp() {
            /** a list of invalid elements and the reasons they aren't valid */
            invalidElements.value = [];
            //TODO: handle fundamental problem of syncing DOM, views, and list here
            /** data for list in progress */
            workingElements.value = [];
            // copy initial list, sort, add ids if needed
            workingElements.value = JSON.parse(JSON.stringify(props.initialElements.slice(0)));
            // reverse the order of the elements to emulate what we have in the history panel
            workingElements.value.reverse();
            // for inListElements, reset their values (in order) to datasets from workingElements
            const inListElementsPrev = inListElements.value;
            inListElements.value = [];
            inListElementsPrev.forEach((prevElem) => {
                const element = workingElements.value.find((e) => e.id === prevElem.id);
                const problem = _isElementInvalid(prevElem);
                if (element) {
                    inListElements.value.push(element);
                }
                else if (problem) {
                    const invalidMsg = `${prevElem.hid}: ${prevElem.name} ${problem} and ${NOT_VALID_ELEMENT_MSG}`;
                    invalidElements.value.push(invalidMsg);
                    Toast.error(invalidMsg, localize("Invalid element"));
                }
                else {
                    const invalidMsg = `${prevElem.hid}: ${prevElem.name} ${localize("has been removed from the collection")}`;
                    invalidElements.value.push(invalidMsg);
                    Toast.error(invalidMsg, localize("Invalid element"));
                }
            });
            // _ensureElementIds();
            _validateElements();
            _mangleDuplicateNames();
        }
        // TODO: not sure if this is needed
        // function _ensureElementIds() {
        //     workingElements.value.forEach((element) => {
        //         if (!Object.prototype.hasOwnProperty.call(element, "id")) {
        //             console.warn("Element missing id", element);
        //         }
        //     });
        //     return workingElements.value;
        // }
        // /** separate working list into valid and invalid elements for this collection */
        function _validateElements() {
            workingElements.value = workingElements.value.filter((element) => {
                var problem = _isElementInvalid(element);
                if (problem) {
                    invalidElements.value.push(element.name + "  " + problem);
                }
                return !problem;
            });
            return workingElements.value;
        }
        /** describe what is wrong with a particular element if anything */
        function _isElementInvalid(element) {
            if (element.history_content_type === "dataset_collection") {
                return localize("is a collection, this is not allowed");
            }
            var validState = element.state === STATES.OK || STATES.NOT_READY_STATES.includes(element.state);
            if (!validState) {
                return localize("has errored, is paused, or is not accessible");
            }
            if (element.deleted || element.purged) {
                return localize("has been deleted or purged");
            }
            // is the element's extension not a subtype of any of the required extensions?
            if (filterExtensions.value &&
                element.extension &&
                !datatypesMapper.value?.isSubTypeOfAny(element.extension, props.extensions)) {
                return localize(`has an invalid format: ${element.extension}`);
            }
            return null;
        }
        /** Show the element's extension next to its name:
         *  1. If there are no required extensions, so users can avoid creating mixed extension lists.
         *  2. If the extension is not in the list of required extensions but is a subtype of one of them,
         *     so users can see that those elements were still included as they are implicitly convertible.
         */
        function showElementExtension(element) {
            return (!props.extensions?.length ||
                (filterExtensions.value &&
                    element.extension &&
                    !props.extensions?.includes(element.extension) &&
                    datatypesMapper.value?.isSubTypeOfAny(element.extension, props.extensions)));
        }
        // /** mangle duplicate names using a mac-like '(counter)' addition to any duplicates */
        function _mangleDuplicateNames() {
            var counter = 1;
            var existingNames = {};
            workingElements.value.forEach((element) => {
                var currName = element.name;
                while (Object.prototype.hasOwnProperty.call(existingNames, currName)) {
                    currName = `${element.name} (${counter})`;
                    counter += 1;
                }
                element.name = currName;
                existingNames[element.name] = true;
            });
        }
        function changeDatatypeFilter(newFilter) {
            _elementsSetUp();
        }
        function elementSelected(e) {
            if (!selectedDatasetElements.value.includes(e.id)) {
                selectedDatasetElements.value.push(e.id);
            }
            else {
                selectedDatasetElements.value.splice(selectedDatasetElements.value.indexOf(e.id), 1);
            }
        }
        function elementDiscarded(e) {
            workingElements.value.splice(workingElements.value.indexOf(e), 1);
            selectedDatasetElements.value = selectedDatasetElements.value.filter((element) => {
                return element !== e.id;
            });
            return workingElements.value;
        }
        function clickClearAll() {
            selectedDatasetElements.value = [];
        }
        function clickRemoveSelected() {
            workingElements.value = workingElements.value.filter((element) => {
                return !selectedDatasetElements.value.includes(element.id);
            });
            selectedDatasetElements.value = [];
        }
        function clickSelectAll() {
            selectedDatasetElements.value = workingElements.value.map((element) => {
                return element.id;
            });
        }
        const { confirm } = useConfirmDialog();
        async function clickedCreate(collectionName) {
            checkForDuplicates();
            const returnedElements = props.fromSelection ? workingElements.value : inListElements.value;
            atLeastOneElement.value = returnedElements.length > 0;
            let confirmed = false;
            if (!atLeastOneElement.value) {
                confirmed = await confirm("Are you sure you want to create a list with no datasets?", {
                    title: "Create an empty list",
                    okTitle: "Create",
                    okVariant: "primary",
                });
            }
            if (state.value !== "error" && (atLeastOneElement.value || confirmed)) {
                emit("clicked-create", returnedElements, collectionName, hideSourceItems.value);
            }
        }
        function checkForDuplicates() {
            var valid = true;
            var existingNames = {};
            duplicateNames.value = [];
            workingElements.value.forEach((element) => {
                if (Object.prototype.hasOwnProperty.call(existingNames, element.name)) {
                    valid = false;
                    duplicateNames.value.push(element.name);
                }
                existingNames[element.name] = true;
            });
            state.value = valid ? "build" : "error";
        }
        /** reset all data to the initial state */
        function reset() {
            /** Ids of elements that have been selected by the user - to preserve over renders */
            selectedDatasetElements.value = [];
            _elementsSetUp();
        }
        function sortByName() {
            workingElements.value.sort(compareNames);
        }
        function compareNames(a, b) {
            if (a.name && b.name && a.name < b.name) {
                return -1;
            }
            if (a.name && b.name && a.name > b.name) {
                return 1;
            }
            return 0;
        }
        function onUpdateHideSourceItems(newHideSourceItems) {
            hideSourceItems.value = newHideSourceItems;
        }
        watch(() => props.initialElements, () => {
            // for any new/removed elements, add them to working elements
            _elementsSetUp();
        }, { immediate: true });
        watch(() => datatypesMapper.value, async (mapper) => {
            if (props.extensions?.length && !mapper) {
                await datatypesMapperStore.createMapper();
            }
        }, { immediate: true });
        function addUploadedFiles(files) {
            const returnedElements = props.fromSelection ? workingElements : inListElements;
            files.forEach((f) => {
                const file = props.fromSelection ? f : workingElements.value.find((e) => e.id === f.id);
                const problem = _isElementInvalid(f);
                if (file && !returnedElements.value.find((e) => e.id === file.id)) {
                    returnedElements.value.push(file);
                }
                else if (problem) {
                    invalidElements.value.push("Uploaded item: " + f.name + "  " + problem);
                    Toast.error(localize(`Dataset ${f.hid}: ${f.name} ${problem} and is an invalid element for this collection`), localize("Uploaded item is invalid"));
                }
                else {
                    invalidElements.value.push("Uploaded item: " + f.name + " could not be added to the collection");
                    Toast.error(localize(`Dataset ${f.hid}: ${f.name} could not be added to the collection`), localize("Uploaded item is invalid"));
                }
            });
        }
        /** find the element in the workingElements array and update its name */
        function renameElement(element, name) {
            element = workingElements.value.find((e) => e.id === element.id);
            if (element) {
                element.name = name;
            }
        }
        //TODO: issue #9497
        // const removeExtensions = ref(true);
        // removeExtensionsToggle: function () {
        //     this.removeExtensions = !this.removeExtensions;
        //     if (this.removeExtensions == true) {
        //         this.removeExtensionsFn();
        //     }
        // },
        // removeExtensionsFn: function () {
        //     workingElements.value.forEach((e) => {
        //         var lastDotIndex = e.lastIndexOf(".");
        //         if (lastDotIndex > 0) {
        //             var extension = e.slice(lastDotIndex, e.length);
        //             e = e.replace(extension, "");
        //         }
        //     });
        // },
        return { __sfc: true, NOT_VALID_ELEMENT_MSG, props, emit, state, duplicateNames, invalidElements, workingElements, selectedDatasetElements, hideSourceItems, atLeastOneElement, atLeastOneDatasetIsSelected, getSelectedDatasetElements, noMoreValidDatasets, returnInvalidElementsLength, returnInvalidElements, noInitialElements, showDuplicateError, allElementsAreInvalid, inListElements, datatypesMapperStore, datatypesMapper, filterExtensions, listHasMixedExtensions, _elementsSetUp, _validateElements, _isElementInvalid, showElementExtension, _mangleDuplicateNames, changeDatatypeFilter, elementSelected, elementDiscarded, clickClearAll, clickRemoveSelected, clickSelectAll, confirm, clickedCreate, checkForDuplicates, reset, sortByName, compareNames, onUpdateHideSourceItems, addUploadedFiles, renameElement, faSquare, faMinus, faSortAlphaDown, faTimes, faUndo, FontAwesomeIcon, BAlert, BButton, BButtonGroup, draggable, localize, FormSelectMany, HelpText, CollectionCreator, DatasetCollectionElementView };
    }
});
